import api from './api';
import errorHandler from '../utils/errorHandler';

class SearchService {
    constructor() {
        this.searchCache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
        this.searchHistory = JSON.parse(localStorage.getItem('search_history') || '[]');
        this.searchSuggestions = [];
    }

    // Advanced search with multiple filters
    async searchVendors(searchParams = {}) {
        try {
            const cacheKey = this.generateCacheKey('vendors', searchParams);
            
            // Check cache first
            if (this.searchCache.has(cacheKey)) {
                const cached = this.searchCache.get(cacheKey);
                if (Date.now() - cached.timestamp < this.cacheTimeout) {
                    return cached.data;
                }
            }

            const response = await api.get('/vendors', { params: searchParams });
            
            // Accept both REST {success,data} and plain pagination arrays
            let data = [];
            if (response?.data?.success) {
                data = response.data.data;
            } else if (Array.isArray(response?.data?.data)) {
                data = response.data; // already array or paginated object
            } else if (Array.isArray(response?.data)) {
                data = response.data;
            } else if (response?.data?.data?.data) {
                // Laravel paginator style { data: { data: [...], total, ... } }
                data = { data: response.data.data.data, total: response.data.data.total };
            } else {
                // Fallback to empty array
                data = { data: [], total: 0 };
            }

            // Cache the result
            this.searchCache.set(cacheKey, {
                data,
                timestamp: Date.now()
            });

            return data;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Vendor search' });
            throw error;
        }
    }

    // Advanced search for menu items
    async searchMenuItems(searchParams = {}) {
        try {
            const cacheKey = this.generateCacheKey('menu', searchParams);
            
            // Check cache first
            if (this.searchCache.has(cacheKey)) {
                const cached = this.searchCache.get(cacheKey);
                if (Date.now() - cached.timestamp < this.cacheTimeout) {
                    return cached.data;
                }
            }

            const response = await api.get('/menus', { params: searchParams });
            
            let data = { data: [], total: 0 };
            
            // Handle Laravel paginator response structure
            if (response?.data?.success && response?.data?.data) {
                const responseData = response.data.data;
                
                // Check if it's a paginator object (has data property)
                if (responseData.data && Array.isArray(responseData.data)) {
                    // Laravel paginator: { data: [...], current_page, total, per_page, ... }
                    data = {
                        data: responseData.data,
                        total: responseData.total || responseData.data.length
                    };
                } else if (Array.isArray(responseData)) {
                    // Direct array response
                    data = {
                        data: responseData,
                        total: responseData.length
                    };
                }
            } else if (Array.isArray(response?.data?.data)) {
                data = {
                    data: response.data.data,
                    total: response.data.data.length
                };
            } else if (Array.isArray(response?.data)) {
                data = {
                    data: response.data,
                    total: response.data.length
                };
            }

            // Cache the result
            this.searchCache.set(cacheKey, {
                data,
                timestamp: Date.now()
            });

            return data;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Menu search' });
            throw error;
        }
    }

    // Get search suggestions
    async getSearchSuggestions(query, type = 'all') {
        try {
            if (!query || query.length < 2) {
                return [];
            }

            const response = await api.get('/search/suggestions', {
                params: { q: query, type }
            });

            if (response.data?.success) {
                this.searchSuggestions = response.data.data;
                return this.searchSuggestions;
            }

            return [];
        } catch (error) {
            // Fallback to local suggestions
            return this.getLocalSuggestions(query, type);
        }
    }

    // Get trending searches
    async getTrendingSearches() {
        try {
            const response = await api.get('/search/trending');
            
            if (response.data?.success) {
                return response.data.data;
            }

            return [];
        } catch (error) {
            return this.getDefaultTrendingSearches();
        }
    }

    // Get popular filters
    async getPopularFilters() {
        try {
            const response = await api.get('/search/filters');
            
            if (response.data?.success) {
                return response.data.data;
            }

            return this.getDefaultFilters();
        } catch (error) {
            return this.getDefaultFilters();
        }
    }

    // Save search to history
    saveSearchHistory(query, type = 'general') {
        if (!query || query.trim().length < 2) return;

        const searchEntry = {
            query: query.trim(),
            type,
            timestamp: Date.now()
        };

        // Remove duplicates
        this.searchHistory = this.searchHistory.filter(
            item => item.query !== searchEntry.query
        );

        // Add to beginning
        this.searchHistory.unshift(searchEntry);

        // Keep only last 20 searches
        this.searchHistory = this.searchHistory.slice(0, 20);

        // Save to localStorage
        localStorage.setItem('search_history', JSON.stringify(this.searchHistory));
    }

    // Get search history
    getSearchHistory() {
        return this.searchHistory;
    }

    // Clear search history
    clearSearchHistory() {
        this.searchHistory = [];
        localStorage.removeItem('search_history');
    }

    // Get local suggestions (fallback)
    getLocalSuggestions(query, type) {
        const suggestions = {
            vendors: [
                'North Indian', 'South Indian', 'Chinese', 'Continental',
                'Italian', 'Mexican', 'Thai', 'Gujarati', 'Punjabi',
                'Bengali', 'Rajasthani', 'Kerala', 'Tamil', 'Telugu'
            ],
            menu: [
                'Biryani', 'Butter Chicken', 'Dal Makhani', 'Paneer Tikka',
                'Chicken Tikka', 'Naan', 'Roti', 'Rice', 'Curry',
                'Tandoori', 'Kebab', 'Samosa', 'Pakora', 'Lassi'
            ],
            cities: [
                'Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad',
                'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Lucknow'
            ]
        };

        const typeSuggestions = suggestions[type] || [
            ...suggestions.vendors,
            ...suggestions.menu,
            ...suggestions.cities
        ];

        return typeSuggestions
            .filter(item => 
                item.toLowerCase().includes(query.toLowerCase())
            )
            .slice(0, 8);
    }

    // Get default trending searches
    getDefaultTrendingSearches() {
        return [
            { query: 'North Indian', count: 1250, type: 'cuisine' },
            { query: 'Wedding Catering', count: 980, type: 'event' },
            { query: 'Corporate Events', count: 750, type: 'event' },
            { query: 'South Indian', count: 680, type: 'cuisine' },
            { query: 'Chinese Food', count: 520, type: 'cuisine' },
            { query: 'Birthday Party', count: 450, type: 'event' },
            { query: 'Continental', count: 380, type: 'cuisine' },
            { query: 'House Party', count: 320, type: 'event' }
        ];
    }

    // Get default filters
    getDefaultFilters() {
        return {
            cuisines: [
                'North Indian', 'South Indian', 'Chinese', 'Continental',
                'Italian', 'Mexican', 'Thai', 'Gujarati', 'Punjabi',
                'Bengali', 'Rajasthani', 'Kerala', 'Tamil', 'Telugu'
            ],
            eventTypes: [
                'Wedding', 'Corporate Event', 'Birthday Party', 'House Party',
                'Anniversary', 'Festival', 'Conference', 'Seminar'
            ],
            priceRanges: [
                { label: 'Under ₹500', min: 0, max: 500 },
                { label: '₹500 - ₹1000', min: 500, max: 1000 },
                { label: '₹1000 - ₹2000', min: 1000, max: 2000 },
                { label: '₹2000 - ₹5000', min: 2000, max: 5000 },
                { label: 'Above ₹5000', min: 5000, max: null }
            ],
            ratings: [
                { label: '4+ Stars', min: 4 },
                { label: '3+ Stars', min: 3 },
                { label: '2+ Stars', min: 2 },
                { label: '1+ Stars', min: 1 }
            ],
            dietaryOptions: [
                'Vegetarian', 'Vegan', 'Gluten Free', 'Jain',
                'Halal', 'Kosher', 'Sugar Free', 'Low Fat'
            ],
            features: [
                'Home Delivery', 'Outdoor Catering', 'Indoor Catering',
                'Live Cooking', 'Buffet Service', 'Plated Service',
                'Party Decoration', 'DJ Service', 'Photography'
            ]
        };
    }

    // Generate cache key
    generateCacheKey(type, params) {
        return `${type}_${JSON.stringify(params)}`;
    }

    // Clear cache
    clearCache() {
        this.searchCache.clear();
    }

    // Get search analytics
    getSearchAnalytics() {
        const history = this.getSearchHistory();
        const analytics = {
            totalSearches: history.length,
            uniqueQueries: new Set(history.map(h => h.query)).size,
            mostSearched: this.getMostSearched(history),
            searchTrends: this.getSearchTrends(history)
        };
        return analytics;
    }

    // Get most searched terms
    getMostSearched(history) {
        const queryCount = {};
        history.forEach(item => {
            queryCount[item.query] = (queryCount[item.query] || 0) + 1;
        });

        return Object.entries(queryCount)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 5)
            .map(([query, count]) => ({ query, count }));
    }

    // Get search trends
    getSearchTrends(history) {
        const now = Date.now();
        const trends = {
            today: 0,
            thisWeek: 0,
            thisMonth: 0
        };

        history.forEach(item => {
            const age = now - item.timestamp;
            if (age < 24 * 60 * 60 * 1000) trends.today++;
            if (age < 7 * 24 * 60 * 60 * 1000) trends.thisWeek++;
            if (age < 30 * 24 * 60 * 60 * 1000) trends.thisMonth++;
        });

        return trends;
    }

    // Smart search with AI-like suggestions
    async smartSearch(query, context = {}) {
        try {
            const response = await api.post('/search/smart', {
                query,
                context,
                user_id: context.user_id,
                location: context.location,
                preferences: context.preferences
            });

            if (response.data?.success) {
                return response.data.data;
            }

            // Fallback to regular search
            return this.searchVendors({ search: query, ...context });
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Smart search' });
            throw error;
        }
    }

    // Voice search (placeholder for future implementation)
    async voiceSearch() {
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            throw new Error('Voice search not supported in this browser');
        }

        return new Promise((resolve, reject) => {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognition = new SpeechRecognition();
            
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                resolve(transcript);
            };

            recognition.onerror = (event) => {
                reject(new Error('Voice recognition failed: ' + event.error));
            };

            recognition.start();
        });
    }
}

// Create singleton instance
const searchService = new SearchService();

export default searchService;
